import { doc, getDoc, setDoc, updateDoc, increment, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { WhatsAppMessageType } from './hybridWhatsAppService';

// واجهة إحصائيات الرسائل
export interface WhatsAppStats {
  totalSent: number;
  sentByType: Record<WhatsAppMessageType, number>;
  sentToday: number;
  sentThisWeek: number;
  sentThisMonth: number;
  lastUpdated: Date;
}

// واجهة سجل الرسالة
export interface WhatsAppMessageLog {
  id: string;
  messageType: WhatsAppMessageType;
  customerName: string;
  customerPhone: string;
  orderId: string;
  sentAt: Date;
  success: boolean;
  error?: string;
}

class WhatsAppStatsService {
  private readonly STATS_COLLECTION = 'whatsapp_stats';
  private readonly LOGS_COLLECTION = 'whatsapp_message_logs';
  private readonly STATS_DOC_ID = 'global_stats';

  // تسجيل رسالة مرسلة
  public async logMessage(
    messageType: WhatsAppMessageType,
    customerName: string,
    customerPhone: string,
    orderId: string,
    success: boolean,
    error?: string
  ): Promise<void> {
    try {
      // إنشاء سجل الرسالة
      const messageLog: Omit<WhatsAppMessageLog, 'id'> = {
        messageType,
        customerName,
        customerPhone,
        orderId,
        sentAt: new Date(),
        success,
        error
      };

      // حفظ السجل في Firestore
      const logsRef = collection(db, this.LOGS_COLLECTION);
      await setDoc(doc(logsRef), messageLog);

      // تحديث الإحصائيات إذا كانت الرسالة نجحت
      if (success) {
        await this.updateStats(messageType);
      }
    } catch (error) {
      console.error('Error logging WhatsApp message:', error);
    }
  }

  // تحديث الإحصائيات
  private async updateStats(messageType: WhatsAppMessageType): Promise<void> {
    try {
      const statsRef = doc(db, this.STATS_COLLECTION, this.STATS_DOC_ID);
      
      // تحديث العدادات
      await updateDoc(statsRef, {
        totalSent: increment(1),
        [`sentByType.${messageType}`]: increment(1),
        sentToday: increment(1),
        sentThisWeek: increment(1),
        sentThisMonth: increment(1),
        lastUpdated: new Date()
      });
    } catch (error: any) {
      // إذا لم توجد الوثيقة، أنشئها
      if (error?.code === 'not-found') {
        await this.initializeStats(messageType);
      } else {
        console.error('Error updating WhatsApp stats:', error);
      }
    }
  }

  // تهيئة الإحصائيات
  private async initializeStats(messageType: WhatsAppMessageType): Promise<void> {
    try {
      const initialStats: WhatsAppStats = {
        totalSent: 1,
        sentByType: {
          deviceReceived: messageType === 'deviceReceived' ? 1 : 0,
          deviceInProgress: messageType === 'deviceInProgress' ? 1 : 0,
          deviceCompleted: messageType === 'deviceCompleted' ? 1 : 0,
          deviceUrgent: messageType === 'deviceUrgent' ? 1 : 0
        },
        sentToday: 1,
        sentThisWeek: 1,
        sentThisMonth: 1,
        lastUpdated: new Date()
      };

      const statsRef = doc(db, this.STATS_COLLECTION, this.STATS_DOC_ID);
      await setDoc(statsRef, initialStats);
    } catch (error) {
      console.error('Error initializing WhatsApp stats:', error);
    }
  }

  // الحصول على الإحصائيات
  public async getStats(): Promise<WhatsAppStats | null> {
    try {
      const statsRef = doc(db, this.STATS_COLLECTION, this.STATS_DOC_ID);
      const statsSnap = await getDoc(statsRef);

      if (statsSnap.exists()) {
        const data = statsSnap.data();
        return {
          ...data,
          lastUpdated: data.lastUpdated?.toDate() || new Date()
        } as WhatsAppStats;
      }

      return null;
    } catch (error) {
      console.error('Error getting WhatsApp stats:', error);
      return null;
    }
  }

  // الحصول على سجلات الرسائل الأخيرة
  public async getRecentLogs(limitCount: number = 50): Promise<WhatsAppMessageLog[]> {
    try {
      const logsRef = collection(db, this.LOGS_COLLECTION);
      const q = query(
        logsRef,
        orderBy('sentAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const logs: WhatsAppMessageLog[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          ...data,
          sentAt: data.sentAt?.toDate() || new Date()
        } as WhatsAppMessageLog);
      });

      return logs;
    } catch (error) {
      console.error('Error getting WhatsApp message logs:', error);
      return [];
    }
  }

  // الحصول على سجلات رسائل عميل معين
  public async getCustomerLogs(customerPhone: string): Promise<WhatsAppMessageLog[]> {
    try {
      const logsRef = collection(db, this.LOGS_COLLECTION);
      const q = query(
        logsRef,
        where('customerPhone', '==', customerPhone),
        orderBy('sentAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const logs: WhatsAppMessageLog[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          ...data,
          sentAt: data.sentAt?.toDate() || new Date()
        } as WhatsAppMessageLog);
      });

      return logs;
    } catch (error) {
      console.error('Error getting customer WhatsApp logs:', error);
      return [];
    }
  }

  // إعادة تعيين الإحصائيات اليومية (يتم استدعاؤها يومياً)
  public async resetDailyStats(): Promise<void> {
    try {
      const statsRef = doc(db, this.STATS_COLLECTION, this.STATS_DOC_ID);
      await updateDoc(statsRef, {
        sentToday: 0,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error resetting daily WhatsApp stats:', error);
    }
  }

  // إعادة تعيين الإحصائيات الأسبوعية (يتم استدعاؤها أسبوعياً)
  public async resetWeeklyStats(): Promise<void> {
    try {
      const statsRef = doc(db, this.STATS_COLLECTION, this.STATS_DOC_ID);
      await updateDoc(statsRef, {
        sentThisWeek: 0,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error resetting weekly WhatsApp stats:', error);
    }
  }

  // إعادة تعيين الإحصائيات الشهرية (يتم استدعاؤها شهرياً)
  public async resetMonthlyStats(): Promise<void> {
    try {
      const statsRef = doc(db, this.STATS_COLLECTION, this.STATS_DOC_ID);
      await updateDoc(statsRef, {
        sentThisMonth: 0,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error resetting monthly WhatsApp stats:', error);
    }
  }
}

// إنشاء مثيل واحد من الخدمة
export const whatsappStatsService = new WhatsAppStatsService();
export default whatsappStatsService;
